import React, { useState, useEffect, useRef } from 'react';
import './App.css';
import 'bootstrap/dist/css/bootstrap.min.css';
import 'bootstrap/dist/js/bootstrap.bundle.min.js';
import 'bootstrap-icons/font/bootstrap-icons.css';
import './responsive.css';
import Dashboard from './pages/Dashboard.jsx';
import Workers from './pages/Workers.jsx';
import Attendance from './pages/Attendance.jsx';
import Payroll from './pages/Payroll.jsx';
import Leave from './pages/Leave.jsx';
import LeaveTypeMaster from './pages/LeaveTypeMaster.jsx';
import HolidayMaster from './pages/HolidayMaster.jsx';
import ShiftMaster from './pages/ShiftMaster.jsx';
import Reports from './pages/Reports.jsx';
import UserManagement from './pages/UserManagement.jsx';
import PaymentTerms from './pages/PaymentTerms.jsx';
import Login from './components/Login.jsx';
import { HRMSProvider } from './context/HRMSContext.jsx';
import { PermissionsProvider, usePermissions } from './context/PermissionsContext.jsx';
import logo from './logo.png';

const navItems = [
  { key: 'dashboard',     label: 'Dashboard',     icon: 'bi-speedometer2',  module: 'dashboard' },
  { key: 'workers',       label: 'Workers',        icon: 'bi-people-fill',   module: 'workers' },
  { key: 'attendance',    label: 'Attendance',     icon: 'bi-calendar-check',module: 'attendance' },
  { key: 'payroll',       label: 'Payroll',        icon: 'bi-cash-coin',     module: 'payroll' },
  { key: 'leave',         label: 'Leave',          icon: 'bi-calendar-x',    module: 'leave' },
  { key: 'leave-types',   label: 'Leave Types',    icon: 'bi-list-check',    module: 'leave_types' },
  { key: 'holidays',      label: 'Holidays',       icon: 'bi-star',          module: 'holidays' },
  { key: 'shifts',        label: 'Shifts',         icon: 'bi-clock',         module: 'shifts' },
  { key: 'payment-terms', label: 'Payment Terms',  icon: 'bi-credit-card',   module: 'worker_payment_terms' },
];

function AppContent() {
  const [currentPage, setCurrentPage] = useState('dashboard');
  const [pageHistory, setPageHistory] = useState(['dashboard']);
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const [langOpen, setLangOpen] = useState(false);
  const langDropdownRef = useRef(null);
  // Language convert disabled - default to English only
  const [lang, setLang] = useState('en');
  // const [lang, setLang] = useState(() => {
  //   return document.cookie.includes('googtrans=') && document.cookie.includes('/th') ? 'th' : 'en';
  // });

  // Ensure English only: clear any previous Google Translate cookies
  useEffect(() => {
    document.cookie = 'googtrans=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/';
    const host = window.location.hostname;
    if (host && host !== 'localhost' && !/^\d+\.\d+\.\d+\.\d+$/.test(host)) {
      document.cookie = 'googtrans=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/; domain=' + host;
      document.cookie = 'googtrans=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/; domain=.' + host;
    }
  }, []);

  useEffect(() => {
    const handleOutsideClick = (e) => {
      if (langDropdownRef.current && !langDropdownRef.current.contains(e.target)) {
        setLangOpen(false);
      }
    };
    if (langOpen) {
      document.addEventListener('mousedown', handleOutsideClick);
    }
    return () => {
      document.removeEventListener('mousedown', handleOutsideClick);
    };
  }, [langOpen]);

  const setTransCookie = (val) => {
    document.cookie = `googtrans=${val}; path=/`;
    const host = window.location.hostname;
    if (host && host !== 'localhost' && !/^\d+\.\d+\.\d+\.\d+$/.test(host)) {
      document.cookie = `googtrans=${val}; path=/; domain=${host}`;
      document.cookie = `googtrans=${val}; path=/; domain=.${host}`;
    }
  };

  const clearTransCookie = () => {
    document.cookie = 'googtrans=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/';
    const host = window.location.hostname;
    if (host && host !== 'localhost' && !/^\d+\.\d+\.\d+\.\d+$/.test(host)) {
      document.cookie = 'googtrans=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/; domain=' + host;
      document.cookie = 'googtrans=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/; domain=.' + host;
    }
  };

  useEffect(() => {
    const handleRejection = (e) => {
      if (e?.reason && (e.reason.message === 'La' || String(e.reason).includes('translate'))) {
        e.preventDefault();
      }
    };
    window.addEventListener('unhandledrejection', handleRejection);
    return () => window.removeEventListener('unhandledrejection', handleRejection);
  }, []);

  const triggerGoogleTranslate = (langCode) => {
    try {
      const select = document.querySelector('.goog-te-combo');
      if (select) {
        select.value = langCode;
        select.dispatchEvent(new Event('change', { bubbles: true }));
        return true;
      }
    } catch (e) {
      console.warn('Google translate dispatch error:', e);
    }
    return false;
  };

  const changeLanguage = (newLang) => {
    setLang(newLang);
    if (newLang === 'en') {
      clearTransCookie();
      const select = document.querySelector('.goog-te-combo');
      if (select && select.value !== '') {
        select.value = '';
        try {
          select.dispatchEvent(new Event('change', { bubbles: true }));
        } catch (_) {}
      }
      window.location.reload();
    } else {
      setTransCookie(`/en/${newLang}`);
      const triggered = triggerGoogleTranslate(newLang);
      if (!triggered) {
        let attempts = 0;
        const interval = setInterval(() => {
          attempts++;
          if (triggerGoogleTranslate(newLang)) {
            clearInterval(interval);
          } else if (attempts >= 5) {
            clearInterval(interval);
            window.location.reload();
          }
        }, 200);
      }
    }
  };
  const { canAccessModule, loading: permissionsLoading, currentUser, setUser } = usePermissions();

  useEffect(() => {
    const token = localStorage.getItem('token');
    if (token && currentUser) setIsAuthenticated(true);
  }, [currentUser]);

  const handleLogin = async (userData, token) => {
    localStorage.setItem('token', token);
    setUser(userData);
    setIsAuthenticated(true);
  };

  const handleLogout = () => {
    localStorage.removeItem('token');
    setIsAuthenticated(false);
    setUser(null);
    setCurrentPage('dashboard');
    setPageHistory(['dashboard']);
  };

  const navigateToPage = (page) => {
    if (page !== currentPage) {
      setCurrentPage(page);
      setPageHistory(prev => [...prev, page]);
      window.history.pushState({ page }, '', `#${page}`);
    }
    setMenuOpen(false);
  };

  if (permissionsLoading) {
    return (
      <div className="d-flex justify-content-center align-items-center vh-100">
        <div className="spinner-border" role="status">
          <span className="visually-hidden">Loading...</span>
        </div>
      </div>
    );
  }

  if (!isAuthenticated) return <Login onLogin={handleLogin} />;

  const renderPage = () => {
    try {
      switch (currentPage) {
        case 'workers':       return <Workers />;
        case 'attendance':    return <Attendance />;
        case 'payroll':       return <Payroll />;
        case 'leave':         return <Leave />;
        case 'leave-types':   return <LeaveTypeMaster />;
        case 'holidays':      return <HolidayMaster />;
        case 'shifts':        return <ShiftMaster />;
        case 'payment-terms': return <PaymentTerms />;
        case 'reports':       return <Reports />;
        case 'users':         return <UserManagement />;
        default:              return <Dashboard lang={lang} onNavigate={navigateToPage} />;
      }
    } catch (error) {
      return <div className="alert alert-danger">Error loading page: {error.message}</div>;
    }
  };

  return (
    <HRMSProvider>
      <div className="hrms-app">
        <nav className="top-navbar">
          <div className="navbar-brand-area">
            <img src={logo} alt="HRMS Logo" className="brand-logo" />
          </div>

          <div className={`nav-links ${menuOpen ? 'open' : ''}`}>
            {navItems.map(item =>
              canAccessModule(item.module) && (
                <button
                  key={item.key}
                  className={`nav-link-btn ${currentPage === item.key ? 'active' : ''}`}
                  onClick={() => navigateToPage(item.key)}
                >
                  <i className={`bi ${item.icon}`}></i>
                  <span>{item.label}</span>
                </button>
              )
            )}
          </div>

          <div className="navbar-right">
            {/* Language Convert Section - Commented out, English only */}
            {/* <div className="lang-dropdown" ref={langDropdownRef}>
              <button className="lang-btn" onClick={() => setLangOpen(!langOpen)} translate="no" type="button">
                <i className="bi bi-globe2"></i>
                <span className="notranslate">{lang === 'th' ? 'ภาษาไทย' : 'English'}</span>
                <i className="bi bi-chevron-down" style={{fontSize:'10px'}}></i>
              </button>
              {langOpen && (
                <ul className="lang-menu">
                  <li>
                    <button
                      type="button"
                      className={lang === 'en' ? 'active' : ''}
                      onMouseDown={(e) => { e.preventDefault(); changeLanguage('en'); setLangOpen(false); }}
                      onClick={() => { changeLanguage('en'); setLangOpen(false); }}
                      translate="no"
                    >
                      <span className="notranslate">English</span>
                    </button>
                  </li>
                  <li>
                    <button
                      type="button"
                      className={lang === 'th' ? 'active' : ''}
                      onMouseDown={(e) => { e.preventDefault(); changeLanguage('th'); setLangOpen(false); }}
                      onClick={() => { changeLanguage('th'); setLangOpen(false); }}
                      translate="no"
                    >
                      <span className="notranslate">ภาษาไทย</span>
                    </button>
                  </li>
                </ul>
              )}
            </div> */}
            <button className="hamburger" onClick={() => setMenuOpen(!menuOpen)}>
              <i className="bi bi-list"></i>
            </button>
            <div className="user-menu">
              <button className="user-btn" onClick={() => setDropdownOpen(!dropdownOpen)}>
                <span className="user-avatar">{currentUser?.username?.[0]?.toUpperCase()}</span>
                <span className="user-name">{currentUser?.username}</span>
                <i className="bi bi-chevron-down" style={{fontSize:'11px'}}></i>
              </button>
              {dropdownOpen && (
                <ul className="user-dropdown">
                  {canAccessModule('user_management') && (
                    <li>
                      <button onClick={() => { navigateToPage('users'); setDropdownOpen(false); }}>
                        <i className="bi bi-person-gear"></i> Users
                      </button>
                    </li>
                  )}
                  {canAccessModule('reports') && (
                    <li>
                      <button onClick={() => { navigateToPage('reports'); setDropdownOpen(false); }}>
                        <i className="bi bi-bar-chart-line"></i> Reports
                      </button>
                    </li>
                  )}
                  <li className="divider"></li>
                  <li>
                    <button onClick={handleLogout}>
                      <i className="bi bi-box-arrow-right"></i> Logout
                    </button>
                  </li>
                </ul>
              )}
            </div>
          </div>
        </nav>

        <main className="main-content">
          {renderPage()}
        </main>
      </div>
    </HRMSProvider>
  );
}

function App() {
  return (
    <PermissionsProvider>
      <AppContent />
    </PermissionsProvider>
  );
}

export default App;
